import React, { createContext, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [permissionsRevoked, setPermissionsRevoked] = useState(false)
  const realtimeChannelsRef = useRef([])
  const currentUserIdRef = useRef(null)
  const profileLoadingRef = useRef(false)
  const userProfileRef = useRef(null)

  useEffect(() => {
    userProfileRef.current = userProfile
  }, [userProfile])

  const profileOperations = {
    async load(userId) {
      if (!userId) {
        setProfileLoading(false)
        return
      }
      if (profileLoadingRef?.current) return
      profileLoadingRef.current = true
      setProfileLoading(true)
      try {
        const { data: profile, error: profileError } = await supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('id', userId)
          ?.single()

        if (profileError && profileError?.code !== 'PGRST116') {
          console.warn('Profile load error:', profileError)
        }

        if (profile) {
          // Fetch org_members — try by user_id first, then email fallback scoped to same org
          let currentMember = null;
          const { data: memberByUserId } = (await supabase
            ?.from('org_members')
            ?.select('role, license_type, org_role_id, organization_id')
            ?.eq('user_id', userId)
            ?.eq('is_active', true)
            ?.single()) || {};

          if (memberByUserId) {
            currentMember = memberByUserId;
          } else if (profile?.email) {
            // Email fallback: MUST scope by organization_id if available to prevent cross-org bleed
            let emailQuery = supabase
              ?.from('org_members')
              ?.select('role, license_type, org_role_id, organization_id')
              ?.eq('email', profile?.email)
              ?.eq('is_active', true)
              ?.order('created_at', { ascending: false })
              ?.limit(1);

            // If profile already has an org, scope to that org only
            if (profile?.organization_id) {
              emailQuery = emailQuery?.eq('organization_id', profile?.organization_id);
            }

            const { data: memberByEmail } = (await emailQuery?.single()) || {};
            if (memberByEmail) {
              currentMember = memberByEmail;
              // Backfill user_id on the org_members row so future lookups use the fast path
              try {
                await supabase
                  ?.from('org_members')
                  ?.update({ user_id: userId })
                  ?.eq('email', profile?.email)
                  ?.eq('organization_id', memberByEmail?.organization_id)
                  ?.is('user_id', null);
              } catch { /* non-critical */ }
            }
          }

          const freshSystemRole = currentMember
            ? ((currentMember?.role === 'owner' || currentMember?.role === 'admin')
                ? 'org_admin' : 'org_member')
            : (profile?.system_role || 'org_member');

          const effectiveOrgRoleId = profile?.org_role_id || currentMember?.org_role_id || null

          if (effectiveOrgRoleId) {
            // ── Parallelized sub-queries (was sequential, now concurrent) ────
            const [permissionsResult, orgRoleResult] = await Promise.all([
              supabase?.from('org_role_permissions')?.select('*')?.eq('org_role_id', effectiveOrgRoleId),
              supabase?.from('org_roles')?.select('name')?.eq('id', effectiveOrgRoleId)?.single(),
            ])

            const { data: permissions } = permissionsResult || {}
            const { data: orgRole } = orgRoleResult || {}

            setUserProfile({
              ...profile,
              system_role: freshSystemRole,
              org_role_id: effectiveOrgRoleId,
              permissions: permissions || [],
              role_name: orgRole?.name || null,
              license_type: currentMember?.license_type || profile?.license_type || 'professional',
            })
          } else {
            setUserProfile({
              ...profile,
              system_role: freshSystemRole,
              permissions: [],
              role_name: null,
              license_type: currentMember?.license_type || profile?.license_type || 'professional',
            })
          }

          if (profile?.system_role !== freshSystemRole) {
            try {
              await supabase?.rpc('sync_user_system_role', { p_user_id: userId })
            } catch (syncErr) {
              console.warn('[Auth] sync_user_system_role RPC failed (non-critical):', syncErr?.message)
            }
          }
        } else {
          // Fallback: check org_members table for legacy support
          const { data: member } = await supabase
            ?.from('org_members')?.select('*, organizations(*)')?.eq('user_id', userId)?.eq('is_active', true)
            ?.single()

          if (member) {
            const systemRole = (member?.role === 'owner' || member?.role === 'admin')? 'org_admin' : 'org_member'

            // ── Parallelized sub-queries ──────────────────────────────────────
            const [permsResult, memberOrgRoleResult] = await Promise.all([
              supabase?.from('org_role_permissions')?.select('*')?.eq('org_role_id', member?.org_role_id),
              supabase?.from('org_roles')?.select('name')?.eq('id', member?.org_role_id)?.single(),
            ])

            const { data: perms } = permsResult || {}
            const { data: memberOrgRole } = memberOrgRoleResult || {}
            const permissions = perms || []
            const roleName = memberOrgRole?.name || null

            const builtProfile = {
              id: userId,
              email: member?.email,
              full_name: member?.full_name,
              system_role: systemRole,
              organization_id: member?.organization_id,
              org_role_id: member?.org_role_id || null,
              role_name: roleName,
              permissions,
            }

            setUserProfile(builtProfile)

            await supabase
              ?.from('user_profiles')
              ?.upsert({
                id: userId,
                email: member?.email,
                full_name: member?.full_name,
                system_role: systemRole,
                organization_id: member?.organization_id,
                org_role_id: member?.org_role_id || null,
                updated_at: new Date()?.toISOString(),
              }, { onConflict: 'id' })
          }
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        profileLoadingRef.current = false
        setProfileLoading(false)
      }
    },

    clear() {
      profileLoadingRef.current = false
      setUserProfile(null)
      userProfileRef.current = null
      setProfileLoading(false)
      setPermissionsRevoked(false)
    }
  }

  // ============================================================
  // REAL-TIME PERMISSION SYNC
  // Consolidated from 4 channels → 1 channel per user.
  // At 5000 users this reduces WebSocket connections by ~15,000.
  // ============================================================

  const setupRealtimePermissionSync = (userId) => {
    cleanupRealtimeChannels()

    const currentOrgRoleId = userProfileRef?.current?.org_role_id

    // Single channel with multiple postgres_changes listeners
    let channel = supabase?.channel(`rbac-${userId}`)

    // 1. user_profiles changes for this user
    channel = channel?.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_profiles',
        filter: `id=eq.${userId}`,
      },
      async (payload) => {
        const prev = userProfileRef?.current
        const next = payload?.new
        if (
          prev?.system_role !== next?.system_role ||
          prev?.org_role_id !== next?.org_role_id ||
          prev?.organization_id !== next?.organization_id ||
          prev?.is_active !== next?.is_active
        ) {
          if (next?.is_active === false) {
            setPermissionsRevoked(true)
          }
          await profileOperations?.load(userId)
        }
      }
    )

    // 2. org_role_permissions changes for the user's role (filtered)
    if (currentOrgRoleId) {
      channel = channel?.on(
        'postgres_changes',
        {
          event: '*',schema: 'public',table: 'org_role_permissions',
          filter: `org_role_id=eq.${currentOrgRoleId}`,
        },
        async () => {
          await profileOperations?.load(userId)
        }
      )

      // 3. org_roles changes — filtered to this user's specific role only
      // (avoids receiving every org's role updates globally)
      channel = channel?.on(
        'postgres_changes',
        {
          event: 'UPDATE',schema: 'public',table: 'org_roles',
          filter: `id=eq.${currentOrgRoleId}`,
        },
        async () => {
          await profileOperations?.load(userId)
        }
      )
    }

    // 4. org_members changes for this user
    channel = channel?.on(
      'postgres_changes',
      {
        event: 'UPDATE',schema: 'public',table: 'org_members',
        filter: `user_id=eq.${userId}`,
      },
      async (payload) => {
        const prev = userProfileRef?.current
        const next = payload?.new
        if (
          prev?.org_role_id !== next?.org_role_id ||
          prev?.is_active !== next?.is_active ||
          (next?.role === 'admin' || next?.role === 'owner') !== (['org_admin', 'superadmin']?.includes(prev?.system_role))
        ) {
          if (next?.is_active === false) {
            setPermissionsRevoked(true)
          }
          await profileOperations?.load(userId)
        }
      }
    )

    channel?.subscribe()
    realtimeChannelsRef.current = [channel]
  }

  const cleanupRealtimeChannels = () => {
    realtimeChannelsRef?.current?.forEach((channel) => {
      supabase?.removeChannel(channel)
    })
    realtimeChannelsRef.current = []
  }

  const authStateHandlers = {
    onChange: (event, session) => {
      const newUser = session?.user ?? null

      // Skip TOKEN_REFRESHED when same user is already loaded — no state change needed
      if (event === 'TOKEN_REFRESHED' && currentUserIdRef?.current && newUser?.id === currentUserIdRef?.current) {
        return
      }

      // Skip SIGNED_IN re-fires on window focus when the same user is already authenticated.
      // Supabase v2 re-emits SIGNED_IN on focus/visibility change; without this guard it
      // triggers setProfileLoading(true) → spinner flash → full subtree remount.
      if (event === 'SIGNED_IN' && currentUserIdRef?.current && newUser?.id === currentUserIdRef?.current) {
        return
      }

      // Guard against spurious SIGNED_OUT events that can fire during token refresh
      // network hiccups. Only sign out if there is no active user currently loaded,
      // or if this is an explicit user-initiated sign-out (no session and no current user).
      if (event === 'SIGNED_OUT' && currentUserIdRef?.current && !newUser) {
        // Supabase sometimes emits SIGNED_OUT transiently during refresh — ignore it
        // if we still have a valid user in memory. The autoRefreshToken will recover.
        console.warn('[Auth] Ignoring spurious SIGNED_OUT event while user is still active')
        return
      }

      setUser(newUser)
      setLoading(false)
      currentUserIdRef.current = newUser?.id ?? null

      if (newUser) {
        setProfileLoading(true)
        profileOperations?.load(newUser?.id)
      } else {
        profileOperations?.clear()
        cleanupRealtimeChannels()
      }
    }
  }

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      if (error) {
        const errMsg = error?.message?.toLowerCase() || ''
        const errName = error?.name || ''

        // Transient network errors — do NOT clear the session.
        // Supabase's autoRefreshToken will retry automatically.
        const isTransientNetworkError =
          errName === 'AuthRetryableFetchError' ||
          errName === 'TypeError' ||
          (error instanceof TypeError) ||
          errMsg?.includes('failed to fetch') ||
          errMsg?.includes('network')

        if (isTransientNetworkError) {
          console.warn('[Auth] Transient network error during getSession — keeping session, autoRefreshToken will retry:', error?.message)
          // Don't clear session — just set loading to false and keep whatever state we have
          setLoading(false)
          return
        }

        // Hard auth errors (invalid/expired refresh token, missing session) — clear and redirect
        const isHardAuthError =
          errMsg?.includes('refresh token') ||
          errName === 'AuthApiError' ||
          errName === 'AuthSessionMissingError'

        if (isHardAuthError) {
          console.warn('[Auth] Hard auth error during getSession, clearing local session:', error?.message)
          try {
            Object.keys(localStorage)?.forEach((key) => {
              if (key?.startsWith('sb-') || key?.includes('supabase')) {
                localStorage?.removeItem(key)
              }
            })
          } catch (_) {}
          authStateHandlers?.onChange(null, null)
        } else {
          authStateHandlers?.onChange(null, session)
        }
      } else {
        authStateHandlers?.onChange(null, session)
      }
    })?.catch((err) => {
      const errName = err?.name || ''
      const errMsg = err?.message?.toLowerCase() || ''

      // Transient network errors — do NOT clear the session
      const isTransientNetworkError =
        errName === 'AuthRetryableFetchError' ||
        errName === 'TypeError' ||(err instanceof TypeError) ||errMsg?.includes('failed to fetch') ||errMsg?.includes('network')

      if (isTransientNetworkError) {
        console.warn('[Auth] Transient network error in getSession catch — keeping session:', errName, errMsg)
        setLoading(false)
        return
      }

      console.warn('[Auth] getSession threw unexpectedly, clearing local session:', errName, errMsg)
      try {
        Object.keys(localStorage)?.forEach((key) => {
          if (key?.startsWith('sb-') || key?.includes('supabase')) {
            localStorage?.removeItem(key)
          }
        })
      } catch (_) {}
      authStateHandlers?.onChange(null, null)
    })

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => {
      subscription?.unsubscribe()
      cleanupRealtimeChannels()
    }
  }, [])

  useEffect(() => {
    if (user?.id && userProfile) {
      setupRealtimePermissionSync(user?.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const signIn = async (email, password) => {
    try {
      const result = await supabase?.auth?.signInWithPassword({ email, password })
      const { data, error } = result || {}
      if (error) return { data: null, error }
      return { data, error: null }
    } catch (err) {
      const msg = err?.message || ''
      const isNetworkError =
        err instanceof TypeError ||
        msg?.toLowerCase()?.includes('failed to fetch') || msg?.toLowerCase()?.includes('network') ||
        err?.name === 'AuthRetryableFetchError'
      if (isNetworkError) {
        return { data: null, error: { message: 'Network error. Please check your connection and try again.' } }
      }
      return { data: null, error: { message: msg || 'An unexpected error occurred. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      cleanupRealtimeChannels()
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) {
        await profileOperations?.load(user?.id)
      }
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const refreshProfile = async () => {
    if (user?.id) {
      await profileOperations?.load(user?.id)
    }
  }

  const dismissPermissionsRevoked = () => {
    setPermissionsRevoked(false)
  }

  // ── Memoized context value ─────────────────────────────────────────────────
  // Prevents all useAuth() consumers from re-rendering on unrelated state changes.
  const value = useMemo(() => ({
    user,
    userProfile,
    loading,
    profileLoading,
    permissionsRevoked,
    dismissPermissionsRevoked,
    signIn,
    signOut,
    updateProfile,
    refreshProfile,
    isAuthenticated: !!user,
    isSuperAdmin: userProfile?.system_role === 'superadmin',
    isOrgAdmin: ['superadmin', 'org_admin']?.includes(userProfile?.system_role),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, userProfile, loading, profileLoading, permissionsRevoked])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuthContext(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: useAuthContext is not implemented yet.', args);
  return null;
}

export { useAuthContext };